import { useEffect, useMemo, useState } from "react";
import { Clock3, Minus, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useTranslation } from "@/hooks/useTranslation";
import { parseDurationSeconds } from "../duration";
import type {
  StrengthExercise,
  StrengthSession,
  StrengthSessionFormData,
  StrengthSetDraft,
  TimedSetGroupDraft,
} from "../types";

interface StrengthSessionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  exercises: StrengthExercise[];
  defaultExerciseId?: number;
  session: StrengthSession | null;
  isSaving: boolean;
  templateMode?: boolean;
  initialTemplateName?: string;
  onSubmit: (
    data: StrengthSessionFormData,
    templateName?: string
  ) => Promise<void>;
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

function createDraft(cvikId?: number): StrengthSetDraft {
  return { cvikId: cvikId?.toString() ?? "", hmotnostKg: "", opakovania: "" };
}

function removeSetIndexFromGroups(
  groups: TimedSetGroupDraft[],
  removedIndex: number
): TimedSetGroupDraft[] {
  return groups
    .map(group => ({
      ...group,
      setIndexes: group.setIndexes
        .filter(index => index !== removedIndex)
        .map(index => (index > removedIndex ? index - 1 : index)),
    }))
    .filter(group => group.setIndexes.length > 0);
}

export function StrengthSessionDialog({
  open,
  onOpenChange,
  exercises,
  defaultExerciseId,
  session,
  isSaving,
  templateMode = false,
  initialTemplateName = "",
  onSubmit,
}: StrengthSessionDialogProps) {
  const { t } = useTranslation();
  const [datum, setDatum] = useState(today());
  const [poznamka, setPoznamka] = useState("");
  const [sets, setSets] = useState<StrengthSetDraft[]>([]);
  const [timedGroups, setTimedGroups] = useState<TimedSetGroupDraft[]>([]);
  const [selectedSetIndexes, setSelectedSetIndexes] = useState<Set<number>>(
    new Set()
  );
  const [selectedDurationSeconds, setSelectedDurationSeconds] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const sessionSets = session?.serie ?? [];
    setDatum(session?.datum ?? today());
    setTemplateName(initialTemplateName);
    setPoznamka(session?.poznamka ?? "");
    setSets(
      sessionSets.map(set => ({
        cvikId: set.cvikId.toString(),
        hmotnostKg: String(set.hmotnostKg),
        opakovania: String(set.opakovania),
      })) ?? [createDraft(defaultExerciseId)]
    );
    setTimedGroups(
      (session?.timedGroups ?? [])
        .map(group => ({
          id: group.id,
          setIndexes: sessionSets.reduce<number[]>((indexes, set, index) => {
            if (set.casovaSkupinaId === group.id) indexes.push(index);
            return indexes;
          }, []),
          trvanieSekund: String(group.trvanieSekund),
        }))
        .filter(group => group.setIndexes.length > 0)
    );
    if (sessionSets.length === 0) setSets([createDraft(defaultExerciseId)]);
    setSelectedSetIndexes(new Set());
    setSelectedDurationSeconds("");
    setError(null);
  }, [open, session, defaultExerciseId, initialTemplateName]);

  const assignedSetIndexes = useMemo(
    () => new Set(timedGroups.flatMap(group => group.setIndexes)),
    [timedGroups]
  );
  const selectedSets = useMemo(
    () =>
      Array.from(selectedSetIndexes)
        .sort((left, right) => left - right)
        .map(index => ({ index, set: sets[index] }))
        .filter(
          (item): item is { index: number; set: StrengthSetDraft } =>
            item.set !== undefined
        ),
    [selectedSetIndexes, sets]
  );
  const selectedExerciseIds = useMemo(
    () => new Set(selectedSets.map(({ set }) => set.cvikId)),
    [selectedSets]
  );

  const updateSet = (index: number, update: Partial<StrengthSetDraft>) => {
    if (update.cvikId !== undefined) {
      setTimedGroups(current =>
        current
          .map(group => ({
            ...group,
            setIndexes: group.setIndexes.filter(setIndex => setIndex !== index),
          }))
          .filter(group => group.setIndexes.length > 0)
      );
      setSelectedSetIndexes(current => {
        const next = new Set(current);
        next.delete(index);
        return next;
      });
    }
    setSets(current =>
      current.map((set, setIndex) =>
        setIndex === index ? { ...set, ...update } : set
      )
    );
  };

  const toggleSetSelection = (index: number, checked: boolean) => {
    setSelectedSetIndexes(current => {
      const next = new Set(current);
      if (checked) next.add(index);
      else next.delete(index);
      return next;
    });
  };

  const addTimedGroup = () => {
    const durationSeconds = parseDurationSeconds(selectedDurationSeconds);
    if (selectedSets.length === 0) return;
    if (selectedExerciseIds.size !== 1) {
      setError(t("strengthProgress:validation.timedGroupMixedExercise"));
      return;
    }
    if (durationSeconds === null) {
      setError(t("strengthProgress:validation.invalidTimedDuration"));
      return;
    }

    setTimedGroups(current => [
      ...current,
      {
        setIndexes: selectedSets.map(({ index }) => index),
        trvanieSekund: selectedDurationSeconds,
      },
    ]);
    setSelectedSetIndexes(new Set());
    setSelectedDurationSeconds("");
    setError(null);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (templateMode && !templateName.trim()) {
      setError(t("strengthProgress:templates.nameRequired"));
      return;
    }
    const serie = sets.map(set => ({
      cvikId: Number(set.cvikId),
      hmotnostKg: Number(set.hmotnostKg),
      opakovania: Number(set.opakovania),
    }));
    const isValid =
      datum.length > 0 &&
      serie.length > 0 &&
      serie.every(
        set =>
          Number.isInteger(set.cvikId) &&
          set.cvikId > 0 &&
          Number.isFinite(set.hmotnostKg) &&
          set.hmotnostKg >= 0 &&
          Number.isInteger(set.opakovania) &&
          set.opakovania > 0
      );
    const hasValidTimedGroups = timedGroups.every(group => {
      const durationSeconds = parseDurationSeconds(group.trvanieSekund);
      const groupExerciseIds = new Set(
        group.setIndexes.map(index => sets[index]?.cvikId)
      );
      return (
        group.setIndexes.length > 0 &&
        group.setIndexes.every(index => sets[index] !== undefined) &&
        groupExerciseIds.size === 1 &&
        durationSeconds !== null
      );
    });

    if (!isValid) {
      setError(t("strengthProgress:validation.invalidSet"));
      return;
    }
    if (!hasValidTimedGroups) {
      setError(t("strengthProgress:validation.invalidTimedGroup"));
      return;
    }

    setError(null);
    const payload = {
      datum,
      poznamka: poznamka.trim() || undefined,
      serie,
      timedGroups: timedGroups.map(group => ({
        id: group.id,
        setIndexes: group.setIndexes,
        trvanieSekund: parseDurationSeconds(group.trvanieSekund)!,
      })),
    };
    if (templateMode) await onSubmit(payload, templateName.trim());
    else await onSubmit(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] grid-rows-[auto_minmax(0,1fr)_auto] gap-0 overflow-hidden p-0 sm:max-w-3xl">
        <DialogHeader className="border-b px-6 py-4 pr-12">
          <DialogTitle>
            {templateMode
              ? t(
                  session
                    ? "strengthProgress:templates.edit"
                    : "strengthProgress:templates.new"
                )
              : session
                ? t("strengthProgress:session.editTitle")
                : t("strengthProgress:session.addTitle")}
          </DialogTitle>
          <DialogDescription>
            {t("strengthProgress:session.description")}
          </DialogDescription>
        </DialogHeader>
        <form
          id="strength-session-form"
          className="min-h-0 min-w-0 space-y-5 overflow-y-auto px-6 py-4"
          onSubmit={handleSubmit}
        >
          <div className="grid min-w-0 gap-4 sm:grid-cols-2">
            {templateMode ? (
              <div className="min-w-0 space-y-2">
                <Label htmlFor="training-template-name">
                  {t("strengthProgress:templates.name")}
                </Label>
                <Input
                  id="training-template-name"
                  maxLength={120}
                  value={templateName}
                  onChange={event => setTemplateName(event.target.value)}
                  required
                />
              </div>
            ) : (
              <div className="min-w-0 space-y-2">
                <Label htmlFor="strength-date">
                  {t("strengthProgress:fields.date")}
                </Label>
                <Input
                  id="strength-date"
                  type="date"
                  value={datum}
                  onChange={event => setDatum(event.target.value)}
                  required
                />
              </div>
            )}
            <div className="min-w-0 space-y-2 sm:col-span-2">
              <Label htmlFor="strength-note">
                {t("strengthProgress:fields.note")}
              </Label>
              <Textarea
                id="strength-note"
                value={poznamka}
                onChange={event => setPoznamka(event.target.value)}
                maxLength={2000}
                placeholder={t("strengthProgress:fields.notePlaceholder")}
              />
            </div>
          </div>

          <div className="min-w-0 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Label>{t("strengthProgress:session.sets")}</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setSets(current => [
                    ...current,
                    createDraft(defaultExerciseId),
                  ])
                }
              >
                <Plus className="mr-2 h-4 w-4" />
                {t("strengthProgress:session.addSet")}
              </Button>
            </div>
            {sets.map((set, index) => {
              const selectId = `timed-set-${index}`;
              const isAssigned = assignedSetIndexes.has(index);
              return (
                <div
                  className="grid min-w-0 grid-cols-[auto_repeat(2,minmax(0,1fr))_auto] items-end gap-3 rounded-lg border p-3 sm:grid-cols-[auto_minmax(0,1fr)_minmax(0,6rem)_minmax(0,6rem)_auto]"
                  key={`${index}-${set.cvikId}`}
                >
                  <div className="col-start-1 row-start-1 flex h-10 items-center">
                    <Checkbox
                      id={selectId}
                      checked={selectedSetIndexes.has(index)}
                      disabled={isAssigned}
                      onCheckedChange={checked =>
                        toggleSetSelection(index, checked === true)
                      }
                      aria-label={t("strengthProgress:session.selectSet")}
                    />
                  </div>
                  <div className="col-span-2 col-start-2 row-start-1 min-w-0 space-y-2 sm:col-span-1">
                    <Label htmlFor={`strength-exercise-${index}`}>
                      {t("strengthProgress:fields.exercise")}
                    </Label>
                    <Select
                      value={set.cvikId}
                      onValueChange={value =>
                        updateSet(index, { cvikId: value })
                      }
                    >
                      <SelectTrigger
                        id={`strength-exercise-${index}`}
                        className="w-full min-w-0 overflow-hidden [&_[data-slot=select-value]]:block [&_[data-slot=select-value]]:min-w-0 [&_[data-slot=select-value]]:truncate"
                      >
                        <SelectValue
                          placeholder={t(
                            "strengthProgress:fields.chooseExercise"
                          )}
                        />
                      </SelectTrigger>
                      <SelectContent className="max-w-[calc(100vw-2rem)]">
                        {exercises.map(exercise => (
                          <SelectItem
                            key={exercise.id}
                            value={exercise.id.toString()}
                          >
                            {exercise.nazov}
                            {!exercise.aktivny
                              ? ` (${t("strengthProgress:archived")})`
                              : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-start-2 row-start-2 min-w-0 space-y-2 sm:col-start-3 sm:row-start-1">
                    <Label htmlFor={`strength-weight-${index}`}>
                      {t("strengthProgress:fields.weight")}
                    </Label>
                    <Input
                      id={`strength-weight-${index}`}
                      className="min-w-0"
                      inputMode="decimal"
                      min="0"
                      step="0.25"
                      type="number"
                      value={set.hmotnostKg}
                      onChange={event =>
                        updateSet(index, { hmotnostKg: event.target.value })
                      }
                    />
                  </div>
                  <div className="col-start-3 row-start-2 min-w-0 space-y-2 sm:col-start-4 sm:row-start-1">
                    <Label htmlFor={`strength-repetitions-${index}`}>
                      {t("strengthProgress:fields.repetitions")}
                    </Label>
                    <Input
                      id={`strength-repetitions-${index}`}
                      className="min-w-0"
                      inputMode="numeric"
                      min="1"
                      step="1"
                      type="number"
                      value={set.opakovania}
                      onChange={event =>
                        updateSet(index, { opakovania: event.target.value })
                      }
                    />
                  </div>
                  <Button
                    aria-label={t("strengthProgress:session.removeSet")}
                    className="col-start-4 row-start-1 sm:col-start-5"
                    disabled={sets.length === 1}
                    size="icon"
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      setSets(current =>
                        current.filter((_, setIndex) => setIndex !== index)
                      );
                      setTimedGroups(current =>
                        removeSetIndexFromGroups(current, index)
                      );
                      setSelectedSetIndexes(current => {
                        const next = new Set<number>();
                        Array.from(current).forEach(setIndex => {
                          if (setIndex < index) next.add(setIndex);
                          if (setIndex > index) next.add(setIndex - 1);
                        });
                        return next;
                      });
                    }}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                </div>
              );
            })}
          </div>

          {selectedSets.length > 0 && (
            <div className="min-w-0 space-y-3 rounded-lg border border-dashed p-4">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Clock3 className="h-4 w-4" />
                {t("strengthProgress:session.selectedSets", {
                  count: selectedSets.length,
                })}
              </div>
              <p className="text-sm text-muted-foreground">
                {t("strengthProgress:session.timedGroupHelp")}
              </p>
              <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-end">
                <div className="w-full min-w-0 space-y-2 sm:max-w-48">
                  <Label htmlFor="timed-set-duration">
                    {t("strengthProgress:fields.durationSeconds")}
                  </Label>
                  <Input
                    id="timed-set-duration"
                    type="text"
                    placeholder="16:53, 1m53s"
                    value={selectedDurationSeconds}
                    onChange={event =>
                      setSelectedDurationSeconds(event.target.value)
                    }
                  />
                </div>
                <Button type="button" onClick={addTimedGroup}>
                  <Clock3 className="mr-2 h-4 w-4" />
                  {t("strengthProgress:session.addTimedGroup")}
                </Button>
              </div>
            </div>
          )}

          {timedGroups.length > 0 && (
            <div className="min-w-0 space-y-3">
              <Label>{t("strengthProgress:session.timedGroups")}</Label>
              {timedGroups.map((group, groupIndex) => (
                <div
                  className="flex min-w-0 flex-col gap-3 rounded-lg border p-3 sm:flex-row sm:items-end"
                  key={`${groupIndex}-${group.setIndexes.join("-")}`}
                >
                  <div className="min-w-0 flex-1 text-sm">
                    {t("strengthProgress:session.timedGroupSummary", {
                      count: group.setIndexes.length,
                    })}
                  </div>
                  <div className="w-full min-w-0 space-y-2 sm:w-40">
                    <Label htmlFor={`timed-group-duration-${groupIndex}`}>
                      {t("strengthProgress:fields.durationSeconds")}
                    </Label>
                    <Input
                      id={`timed-group-duration-${groupIndex}`}
                      type="text"
                      placeholder="16:53, 1m53s"
                      value={group.trvanieSekund}
                      onChange={event => {
                        event.currentTarget.setCustomValidity(
                          parseDurationSeconds(event.target.value) === null
                            ? t(
                                "strengthProgress:validation.invalidTimedDuration"
                              )
                            : ""
                        );
                        setTimedGroups(current =>
                          current.map((currentGroup, index) =>
                            index === groupIndex
                              ? {
                                  ...currentGroup,
                                  trvanieSekund: event.target.value,
                                }
                              : currentGroup
                          )
                        );
                      }}
                    />
                  </div>
                  <Button
                    aria-label={t("strengthProgress:session.removeTimedGroup")}
                    size="icon"
                    type="button"
                    variant="ghost"
                    onClick={() =>
                      setTimedGroups(current =>
                        current.filter((_, index) => index !== groupIndex)
                      )
                    }
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </form>
        <DialogFooter className="border-t bg-background px-6 py-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {t("common:cancel")}
          </Button>
          <Button
            disabled={isSaving || exercises.length === 0}
            form="strength-session-form"
            type="submit"
          >
            {session ? t("common:update") : t("common:create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
